import React, { useState } from 'react';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import CtaBand from '@/components/CtaBand';
import { Phone, Mail, MapPin, Loader2, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent! We’ll be in touch.');
      setDone(true);
    } catch {
      toast.error('Failed to send. Please call us at 561-884-8900.');
    } finally { setSending(false); }
  };
  return (
    <Layout>
      <PageHeader eyebrow="Contact Us" title="Let's talk about your project." subtitle="Call, email, or send us a message — our team responds within 15 minutes during business hours." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-4 lg:col-span-1">
          <div className="rounded-2xl border border-[#E6E9EE] p-5">
            <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-[#C8922F]" /><div><div className="text-xs text-slate-500">Phone</div><a href="tel:5618848900" className="font-semibold text-[#17352D]">561-884-8900</a></div></div>
          </div>
          <div className="rounded-2xl border border-[#E6E9EE] p-5">
            <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-[#C8922F]" /><div><div className="text-xs text-slate-500">Email</div><a href="mailto:support@Royalpalmservice.com" className="font-semibold text-[#17352D] break-all">support@Royalpalmservice.com</a></div></div>
          </div>
          <div className="rounded-2xl border border-[#E6E9EE] p-5">
            <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#C8922F] mt-0.5" /><div><div className="text-xs text-slate-500">Service Area</div><div className="text-sm text-slate-700">Palm Beach • Broward • Martin • St. Lucie • Miami-Dade</div></div></div>
          </div>
          <div className="rounded-2xl border border-[#E6E9EE] p-5">
            <div className="text-xs text-slate-500">Business Hours</div>
            <div className="text-sm text-slate-700 mt-1">Mon–Sat: 7:00am – 7:00pm<br />24/7 Emergency Service</div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {done ? (
            <div className="rounded-2xl border border-[#E6E9EE] bg-[#F2F7F5] p-8 text-center">
              <Check className="h-10 w-10 text-[#17352D] mx-auto" />
              <h2 className="mt-3 font-headline text-2xl text-[#17352D]">Thanks, {form.name.split(' ')[0]}!</h2>
              <p className="text-slate-700 mt-2">We received your message and will follow up shortly. For urgent matters, call 561-884-8900.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-[#E6E9EE] bg-white p-6 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label htmlFor="name">Name *</Label><Input id="name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="contact-name-input" /></div>
                <div><Label htmlFor="email">Email *</Label><Input id="email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="contact-email-input" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label htmlFor="phone">Phone</Label><Input id="phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="contact-phone-input" /></div>
                <div><Label htmlFor="subject">Subject</Label><Input id="subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="mt-1 h-11 rounded-xl" data-testid="contact-subject-input" /></div>
              </div>
              <div><Label htmlFor="msg">Message *</Label><Textarea id="msg" required rows={6} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1 rounded-xl" data-testid="contact-message-input" /></div>
              <Button type="submit" disabled={sending} data-testid="contact-submit-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12 px-6">{sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : 'Send Message'}</Button>
            </form>
          )}
        </div>
      </section>
      <CtaBand />
    </Layout>
  );
}
